import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The period every page reads, kept in the address (`?preset=THIS_MONTH`, `?period=DAILY&date=…`,
 * `?period=MONTHLY&month=…`, `?period=CUSTOM&from=…&to=…`) so a link, a reload and the back button keep it.
 * The API resolves it on the company's calendar and time zone, and answers with the period it read.
 */

export const PRESETS = ['TODAY', 'YESTERDAY', 'THIS_WEEK', 'LAST_WEEK', 'THIS_MONTH', 'LAST_MONTH', 'LAST_90_DAYS', 'THIS_YEAR'] as const;
export type Preset = (typeof PRESETS)[number];
export type PeriodType = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'CUSTOM';

export interface PeriodParams {
  preset?: string;
  period?: string;
  date?: string;
  month?: string;
  from?: string;
  to?: string;
}

const KEYS = ['preset', 'period', 'date', 'month', 'from', 'to'] as const;
export const DEFAULT_PRESET: Preset = 'THIS_MONTH';

/** The period as the API answers it (CR-008 §5.1). */
export interface Span {
  start: string;
  end: string;
  last: string;
  days: number;
}

export interface Period extends Span {
  type: PeriodType;
  preset: Preset | null;
  timezone: string;
  live: boolean;
  elapsed: Span;
  comparison: Span & { basis: string };
}

export function usePeriod() {
  const [search, setSearch] = useSearchParams();
  const params = useMemo<PeriodParams>(() => {
    const out: PeriodParams = {};
    for (const k of KEYS) {
      const v = search.get(k);
      if (v) out[k] = v;
    }
    if (!out.preset && !out.period) out.preset = DEFAULT_PRESET;
    return out;
  }, [search]);

  /** Replaces the period, keeping every other part of the address (filters, tabs). */
  const setPeriod = useCallback(
    (next: PeriodParams) => {
      setSearch(
        (prev) => {
          const out = new URLSearchParams(prev);
          for (const k of KEYS) out.delete(k);
          for (const [k, v] of Object.entries(next)) if (v) out.set(k, v);
          out.delete('page');
          return out;
        },
        { replace: true },
      );
    },
    [setSearch],
  );

  /** The address's other params (a filter, a tab), read and written beside the period. */
  const param = useCallback((key: string) => search.get(key) ?? '', [search]);
  const setParam = useCallback(
    (key: string, value: string | null) => {
      setSearch(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (value) out.set(key, value);
          else out.delete(key);
          if (key !== 'page') out.delete('page');
          return out;
        },
        { replace: true },
      );
    },
    [setSearch],
  );

  /** The period's part of the address, to keep it on a link to another page. */
  const query = useMemo(() => {
    const out = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) out.set(k, v);
    return out.toString();
  }, [params]);

  return { params, setPeriod, param, setParam, query };
}
